import { t } from "@lingui/macro";
import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useAtom, useSetAtom } from "jotai";
import { useCallback, useState } from "react";
import { useFragment } from "react-relay";

import {
  departmentToScheduleOption,
  ScheduleOption,
  ScheduleSwitcher,
} from "@/components/ScheduleSwitcher";
import { useScheduleNavigator } from "@/hooks/useScheduleNavigator";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { ScreenFooter } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenFooter";
import { Button } from "@whoosh/design-system/src/components/controls";
import { HoleCount } from "@whoosh/design-system/src/components/icons_v2/courseLayout";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  Box,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { courseAvailabilityQueryInputAtom } from "../atoms/courseAvailabilityQueryInputs";
import { courseFilterAtom } from "../atoms/courseFilterAtom";
import { DepartmentAvailabilityCoursePicker } from "./DepartmentAvailabilityCoursePicker";
import { DepartmentAvailabilityGroupSizeSelect } from "./DepartmentAvailabilityGroupSizeSelect";
import { DepartmentAvailabilityHoleCountSelect } from "./DepartmentAvailabilityHoleCountPicker";
import { DepartmentAvailabilityTimeSlider } from "./DepartmentAvailabilityTimeSlider";
import { DepartmentAvailabilityFiltersModal_department$key } from "./__generated__/DepartmentAvailabilityFiltersModal_department.graphql";
import { DepartmentAvailabilityFiltersModal_session$key } from "./__generated__/DepartmentAvailabilityFiltersModal_session.graphql";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  departmentRef: DepartmentAvailabilityFiltersModal_department$key | null;
  sessionRef: DepartmentAvailabilityFiltersModal_session$key | null;
};

export const DepartmentAvailabilityFiltersModal = ({
  isOpen,
  onClose,
  departmentRef,
  sessionRef,
}: Props) => {
  const route =
    useRoute<
      RouteProp<ClubStackNavigatorParamList, "DepartmentAvailability">
    >();
  const date = route?.params?.date;

  const session = useFragment(
    DepartmentAvailabilityFiltersModalSessionFragmentString,
    sessionRef
  );
  const department = useFragment(
    DepartmentAvailabilityFiltersModalFragmentString,
    departmentRef
  );

  const [globalFilterState, setGlobalFilterState] = useAtom(
    courseAvailabilityQueryInputAtom
  );

  const setCourseFilter = useSetAtom(courseFilterAtom);

  const [localFilterState, setLocalFilterState] = useState({
    ...globalFilterState,
  });

  const onSave = useCallback(() => {
    setGlobalFilterState(localFilterState);
    onClose();
  }, [localFilterState, onClose, setGlobalFilterState]);

  const onReset = useCallback(() => {
    setLocalFilterState({});
    setCourseFilter([]);
  }, [setCourseFilter]);

  const onHoleCountChange = (value: HoleCount | null) => {
    setLocalFilterState((prev) => {
      return {
        ...prev,
        holeCount: value ?? undefined,
      };
    });
  };

  const onGroupSizeChange = (value: number | undefined) => {
    setLocalFilterState((prev) => {
      return {
        ...prev,
        groupSize: value,
      };
    });
  };

  const onTimeOfDayChange = ({
    timeLTE,
    timeGTE,
  }: {
    timeLTE?: string;
    timeGTE?: string;
  }) => {
    if (!timeLTE && !timeGTE) {
      setLocalFilterState((prev) => {
        return {
          ...prev,
          timeRange: undefined,
        };
      });
    } else {
      setLocalFilterState((prev) => {
        return {
          ...prev,
          timeRange: {
            timeLTE,
            timeGTE,
          },
        };
      });
    }
  };

  const navigateToSchedule = useScheduleNavigator();

  const onScheduleSwitcherChange = (option: ScheduleOption) => {
    const curDate = new Date();
    const todayString = format(curDate, "yyyy-MM-dd");
    const navigateDate = date ? date : todayString;
    navigateToSchedule(option.type, option.slug, navigateDate);
  };

  const selectedScheduleOption = departmentToScheduleOption(department ?? null);

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      upperTitle={t`Filters`}
      hideFooterPadding
      content={
        <VStack flex={1} px={6} gap={10}>
          <ScheduleSwitcher
            selectedOption={selectedScheduleOption}
            onChange={onScheduleSwitcherChange}
            sessionRef={(session as any) ?? null}
          />
          <DepartmentAvailabilityCoursePicker
            departmentRef={(department as any) ?? null}
          />

          <DepartmentAvailabilityGroupSizeSelect
            groupSize={localFilterState.groupSize}
            departmentRef={(department as any) ?? null}
            onChange={onGroupSizeChange}
          />
          <DepartmentAvailabilityHoleCountSelect
            onChange={onHoleCountChange}
            holeCount={localFilterState.holeCount}
          />
          <DepartmentAvailabilityTimeSlider
            timeRange={localFilterState.timeRange}
            departmentRef={(department as any) ?? null}
            onChange={onTimeOfDayChange}
          />
          <Box height={140} />
        </VStack>
      }
      footerContent={
        <ScreenFooter>
          <HStack flex={1} justifyContent="space-between">
            <Box alignSelf="flex-start">
              <Button
                onPress={onReset}
                variant="ghost"
                accessibilityLabel={t`Reset`}
                testID="department-availability-filters-reset-button"
              >{t`Reset`}</Button>
            </Box>
            <Box alignSelf="flex-end">
              <Button
                onPress={onSave}
                accessibilityLabel={t`Save and Close`}
                testID="department-availability-filters-save-and-close-button"
              >{t`Save and Close`}</Button>
            </Box>
          </HStack>
        </ScreenFooter>
      }
    />
  );
};

const DepartmentAvailabilityFiltersModalFragmentString = graphql`
  fragment DepartmentAvailabilityFiltersModal_department on Department {
    ...DepartmentAvailabilityTimeSliderFragment_department
    ...DepartmentAvailabilityGroupSizeSelect_department
    ...DepartmentAvailabilityCoursePicker_department
    ...departmentToScheduleOption_department
  }
`;

const DepartmentAvailabilityFiltersModalSessionFragmentString = graphql`
  fragment DepartmentAvailabilityFiltersModal_session on Session {
    ...ScheduleSwitcher_session
  }
`;
